import React from "react";

import Box, { BoxProps } from "@mui/material/Box";
import Skeleton, { SkeletonProps } from "@mui/material/Skeleton";
import styled from "@mui/material/styles/styled";
import Tooltip from "@mui/material/Tooltip";

import { MOBILE_NAV_HEIGHT } from "@app/analytics/components/MobileNav/styles";
import { BaseOuterContainer } from "@app/analytics/components/reports/styles";

import Stack, { Row, StackProps } from "@rui/Stack";

const HEADER_HEIGHT = {
  tabs: {
    mobile: `${MOBILE_NAV_HEIGHT + 104}px`, // 104 = main header (60px) + tabs (44px)
    desktop: "128px",
  },
  noTabs: {
    mobile: `${MOBILE_NAV_HEIGHT + 60}px`,
    desktop: "74px", // (128 - 52 for tabs + 2 for top nav bottom border)
  },
};

export const FLOATING_NAV_HEIGHT = "56px";

type OuterContainerProps = StackProps & {
  heightOffset?: number;
  withTabs?: boolean;
};

export const MeetingReportOuterContainer = styled(
  ({ withTabs: _, heightOffset: __, ...props }: OuterContainerProps) => (
    <BaseOuterContainer {...props} />
  )
)(({ heightOffset = 0, theme, withTabs = false }) => {
  const headerHeight = withTabs ? HEADER_HEIGHT.tabs : HEADER_HEIGHT.noTabs;
  return {
    backgroundColor: "#FFF",
    "& .meeting-report__inner-container": {
      height: `calc(100vh - ${headerHeight.mobile} - ${heightOffset}px)`,
      [theme.breakpoints.up("mobile")]: {
        height: `calc(100vh - ${headerHeight.desktop} - ${heightOffset}px)`,
      },
    },
    "& .meeting-report__header": {
      height: headerHeight.mobile,
      [theme.breakpoints.up("mobile")]: {
        height: headerHeight.desktop,
      },
    },
    ...(!withTabs && {
      "& .meeting-report__top-nav": { paddingTop: 0 },
      "& .meeting-report__top-nav__tabs": {
        minHeight: "0px",
        "& .MuiTab-root": { display: "none" },
      },
    }),
  };
});

const TopNavContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff",
  paddingTop: "4px",
  borderBottom: `2px solid ${theme.palette.shades.neutral[100]}`,
  "& .meeting-report__header__feedback": {
    display: "none",
  },
  [theme.breakpoints.up("lg")]: {
    "& .meeting-report__header__feedback": {
      display: "flex",
    },
  },
}));

export const TopNav = ({ children }: { children: React.ReactNode }) => (
  <TopNavContainer className="meeting-report__top-nav">
    <Row variant="apart" sx={{ mb: "-2px", px: { xs: 2 } }}>
      {children}
    </Row>
  </TopNavContainer>
);

export const MainContentContainer = styled(Stack)({
  marginTop: `-${FLOATING_NAV_HEIGHT}`,
});

export const FilterCount = styled((props: StackProps) => (
  <Row variant="center" {...props} />
))(({ theme }) => ({
  marginLeft: theme.spacing(1),
  backgroundColor: theme.palette.shades.primary[500],
  height: "18px",
  minWidth: "18px",
  padding: "2px",
  color: "#FFF",
  fontSize: "14px !important",
  borderRadius: "4px",
}));

export const LoadingSkeleton = styled((props: SkeletonProps) => (
  <Skeleton variant="rectangular" {...props} />
))({
  borderRadius: "8px",
});

type DisabledOverlayProps = BoxProps & {
  tooltip?: string;
  variant?: "dark" | "light";
};

export const DisabledOverlay = styled<React.FC<DisabledOverlayProps>>(
  ({ tooltip, variant: _v, ...props }) => (
    <Tooltip title={tooltip} followCursor arrow>
      <Box {...props} />
    </Tooltip>
  )
)(({ theme, variant = "dark" }) => ({
  position: "absolute",
  transition: "opacity 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
  backgroundColor:
    variant === "dark"
      ? theme.palette.transparent.black[25]
      : theme.palette.transparent.black[12],
  zIndex: 3,
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  "&.hidden": {
    zIndex: -1,
    pointerEvents: "auto",
    opacity: 0,
  },
}));
